import React from 'react';
import { Sparkles } from 'lucide-react';

/**
 * Full-screen loading overlay used for long-running actions like
 * AI report generation. Communicates both progress and expected duration
 * so users don't think the app has crashed.
 *
 * Props:
 *  - title:   headline shown under the spinner
 *  - message: subtext explaining the wait
 *  - testId:  data-testid for the container (default: fullscreen-loader)
 */
export default function FullScreenLoader({
  title = 'Working on it…',
  message = 'This can take up to 60 seconds. Please don\u2019t close this window.',
  testId = 'fullscreen-loader',
}) {
  return (
    <div
      className="fixed inset-0 z-[100] bg-gradient-to-br from-white/95 via-primary/5 to-secondary/10 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      <div className="text-center max-w-md w-full">
        {/* Ring spinner with center sparkle */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 rounded-full border-4 border-primary/20"></div>
          <div className="absolute inset-0 rounded-full border-4 border-t-primary border-r-secondary border-b-transparent border-l-transparent animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
        </div>

        <h2 className="text-2xl md:text-3xl font-heading font-bold mb-3">
          <span className="text-gradient">{title}</span>
        </h2>
        <p className="text-slate-600 leading-relaxed">
          {message}
        </p>

        {/* Animated dot progress */}
        <div className="flex justify-center gap-2 mt-6" aria-hidden="true">
          <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0ms' }}></span>
          <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '150ms' }}></span>
          <span className="w-2 h-2 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '300ms' }}></span>
        </div>
      </div>
    </div>
  );
}
